import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { PlusOutlined } from '@ant-design/icons';
import { AiOutlineFileImage, AiOutlineVideoCamera } from 'react-icons/ai';

import DragSortListWrap, { OnDragEndResult } from '../../DragSortListWrap';
import ImageCropper from './ImageCropper';
import Preview from './Preview';
import useAttachmentListState from './hooks/useAttachmentList';
import useImageUpload from './hooks/useImageUpload';
import useVideoUpload from './hooks/useVideoUpload';
import { appActions } from '../../../store/modules/app';
import { getAllowedImageExtensions } from './utils';
import { Attachment, Restriction } from './types';
import { ATTACHMENT_TYPE } from './constants';
import { ImgContainerStyle } from './style';

export interface ImageAndVideoUploadRef {
  value?: Attachment[];
  onChange?: (attachmentList: Attachment[]) => void;
}

export interface ImageAndVideoUploadProps {
  modalTitle: string;
  /**
   * 업로드된 이미지
   */
  value?: Attachment[];
  /**
   * 이미지 편집 가능 여부 확인
   */
  isEditable?: boolean;
  /**
   * 비디오 업로드 가능 여부 확인
   */
  canAddVideo?: boolean;
  /**
   * 비디오만 업로드 가능하도록 설정
   */
  onlyVideo?: boolean;
  /**
   * 업로드 가능 최대 개수
   */
  maxCnt?: number;
  /**
   * gif 파일 업로드 가능 여부 확인
   */
  enableGif?: boolean;
  /**
   * 이미지 최대 용량 제한
   */
  capacityLimit?: number;
  /**
   * 직사각 이미지 크롭 활성화 여부 확인
   */
  cropMode?: boolean;
  /**
   * 이미지 크기를 제한하기 위해 사용
   * - square: 이미지가 정사각 이미지인지 확인하는 속성
   * - widthMin: 이미지 최소 너비를 제한하는 속성
   * - heightMin: 이미지 최소 높이를 제한하는 속성
   * - widthMax: 이미지 최대 너비를 제한하는 속성
   * - heightMax: 이미지 최대 높이를 제한하는 속성
   * - width: 정확한 이미지의 너비를 맞추기 위해 사용하는 속성
   * - height: 정확한 이미지의 높이를 맞추기 위해 사용하는 속성
   */
  restriction?: Restriction;
  /**
   * 다중 이미지 업로드 여부 확인
   */
  multiple?: boolean;
  /**
   * 이미지 변경 콜백 함수
   * @param attachmentList
   */
  onChange?: (attachmentList: Attachment[]) => void;
}

/**
 * 이미지(정사각형, 직사각형)과 비디오를 업로드 할때 사용하는 컴포넌트 입니다
 * - 이미지 업로드 시 v2 API 를 사용하였습니다
 * - cropMode 를 통해 직사각형 이미지를 정사각형으로 crop 할 수 있습니다
 * - cropMode 일 경우 multiple 이 true 일 경우에도 한개의 이미지만 업로드 가능합니다
 * - gif 파일은 enableGif 가 true 일 경우에만 업로드 가능합니다
 * - restriction 를 통해 이미지 사이즈를 제한할 수 있습니다
 * - onlyVideo, canAddVideo 를 통해 비디오를 업로드 가능합니다
 * @param props
 * @constructor
 */
const ImageAndVideoUploadV2 = forwardRef<ImageAndVideoUploadRef, ImageAndVideoUploadProps>(
  (props: ImageAndVideoUploadProps, ref): JSX.Element => {
    const {
      modalTitle,
      value = [],
      isEditable = true,
      canAddVideo = false,
      onlyVideo = false,
      maxCnt = 1,
      enableGif = false,
      capacityLimit,
      cropMode = false,
      restriction,
      multiple = false,
      onChange,
    } = props;
    const { uploadSuccessAttachment, changeOrderAttachment, deleteAttachment } = useAttachmentListState(
      value,
      onChange,
    );
    const acceptExtensions = useMemo(
      () =>
        getAllowedImageExtensions(enableGif)
          .filter((extension) => extension !== 'jpg')
          .map((extension) => `image/${extension}`)
          .join(', '),
      [enableGif],
    );

    useImperativeHandle(ref, () => ({
      value,
      onChange,
    }));

    const [cropFile, setCropFile] = useState<{ index: number; file: File } | null>(null);

    const { uploadImage, uploadCroppedImage } = useImageUpload({
      attachmentList: value,
      uploadSuccessAttachment,
      maxCnt,
      enableGif,
      capacityLimit,
      cropMode,
      restriction,
      setCropFile,
    });
    const { uploadVideo } = useVideoUpload({ uploadSuccessAttachment });

    const dispatch = useDispatch();

    const previewAttachment = (data: Attachment) => {
      let name;
      if (data.type === ATTACHMENT_TYPE.VIDEO) {
        name = 'videoViewModal';
      } else {
        name = 'imgViewModal';
      }
      dispatch(
        appActions.appToggleModal({
          name,
          data: {
            url: data.url,
          },
        }),
      );
    };

    return (
      <div className="img-upload">
        {/* 크롭 모달 */}
        {!!cropFile && (
          <ImageCropper
            modalTitle={modalTitle}
            closeModal={() => setCropFile(null)}
            files={cropFile.file}
            previewImage={previewAttachment}
            rotatable={true}
            uploadCroppedImage={(file) => uploadCroppedImage(file, cropFile.index)}
          />
        )}
        <ImgContainerStyle>
          <div className="wrap-img">
            <DragSortListWrap
              onDragEnd={(result: OnDragEndResult) => {
                isEditable ? changeOrderAttachment(result) : null;
              }}
              list={value}
              item={(data: Attachment, index: number) => {
                return (
                  <Preview
                    data={data}
                    index={index}
                    isOrder={value.length > 1}
                    isEditable={isEditable}
                    uploadVideo={uploadVideo}
                    uploadImage={uploadImage}
                    previewAttachment={previewAttachment}
                    deleteAttachment={deleteAttachment}
                  />
                );
              }}
            />
            {/*이미지 추가*/}
            {isEditable && (
              <div
                className={cx('register-img-wrap', {
                  hide: value.length >= maxCnt,
                })}
              >
                <div className="register-img">
                  <a className="add-btn">
                    {canAddVideo || onlyVideo ? (
                      <>
                        {!onlyVideo && (
                          <div className="add-img-btn">
                            <AiOutlineFileImage />
                          </div>
                        )}
                        <div
                          className={cx('add-video-btn', {
                            'only-video': onlyVideo,
                          })}
                          onClick={() => uploadVideo(value.length)}
                        >
                          <AiOutlineVideoCamera />
                        </div>
                      </>
                    ) : (
                      <PlusOutlined rev={undefined} />
                    )}
                  </a>
                  {!onlyVideo && (
                    <input
                      className={cx('file-input', {
                        'can-add-video': canAddVideo,
                      })}
                      accept={acceptExtensions}
                      type="file"
                      id="fileUpload"
                      multiple={multiple && !cropMode}
                      onChange={uploadImage}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </ImgContainerStyle>
      </div>
    );
  },
);
ImageAndVideoUploadV2.displayName = 'ImageAndVideoUploadV2';

export default ImageAndVideoUploadV2;
